import { Component, computed, inject, input, output } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

export type StatusMessageVariant = 'success' | 'error';

/**
 * Centred card for single message pages (registration complete, subscription
 * success, email verified, ...): round check icon (or "!" for errors), an
 * optional heading, the message, an optional action button and an optional
 * warning. Anything projected goes between the message and the action.
 *
 * message and warning are rendered as html, so translations with links work;
 * only pass trusted strings.
 *
 *   <hrra-status-message
 *     [message]="'text.ActivationPageLinkInformation' | translate"
 *     [actionLabel]="'label.ResendActivationEmail' | translate"
 *     [warning]="maxAttemptsReached() ? ('message.MaxNumberOfAttemptsReached' | translate) : null"
 *     (action)="onResend()">
 *   </hrra-status-message>
 *
 * The card only sets its own width; the page shell (background, gutters) is
 * left to the page.
 */
@Component({
  selector: 'hrra-status-message',
  templateUrl: './status-message.component.html',
  styleUrls: ['./status-message.component.scss'],
})
export class StatusMessageComponent {
  private sanitizer = inject(DomSanitizer);

  variant = input<StatusMessageVariant>('success');
  heading = input<string | null>();
  message = input<string | null>();
  warning = input<string | null>();

  /** label of the button under the message; no button while it is empty */
  actionLabel = input<string | null>();

  action = output<void>();

  messageHtml = computed(() => this.trust(this.message()));
  warningHtml = computed(() => this.trust(this.warning()));

  private trust(html: string | null | undefined) {
    return html ? this.sanitizer.bypassSecurityTrustHtml(html) : null;
  }
}
